/**
 * Merchant configuration service — manages the merchant's display name
 * and allowed-origins allowlist for the XEPPT checkout button.
 *
 * AUTH: Bearer access token from localStorage 'client-metadata', same
 * pattern as webhook_service.ts, sandbox_service.ts, and payout_service.ts.
 *
 * API CONTRACT:
 *   GET  /account/merchant-config[?environment=test|live]
 *     → { display_name: string, allowed_domains: string[], environment: string, logo?: string }
 *
 *   PATCH /account/merchant-config[?environment=test|live]
 *     body: { display_name?: string, allowed_domains?: string[], logo?: string }  (partial — send only changed field)
 *     → { display_name: string, allowed_domains: string[], environment: string, logo?: string }
 *     400 on validation error with { field, value } or { error } detail
 *
 *   `logo` (as returned by GET/PATCH) is an absolute https:// S3 URL.
 *
 *   PATCH /account/merchant-config/logo[?environment=test|live]
 *     body: { logo: string }  (raw base64-encoded image bytes — NOT a data: URL;
 *       strip any "data:image/...;base64," prefix before sending, same as the
 *       avatar upload path)
 *     → { display_name: string, allowed_domains: string[], environment: string, logo: string }
 *     400 on validation error with { field, value } or { error } detail
 *
 * Graceful degradation: callers must treat MerchantConfigApiError for any
 * non-200 GET response as "config unavailable" (backend may not yet be deployed)
 * and fall back to the read-only display rather than crashing.
 */

import type { Environment } from '@context/environment_context';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? '';

function authHeaders(): Record<string, string> {
    try {
        const raw = localStorage.getItem('client-metadata');
        if (!raw) return {};
        const meta = JSON.parse(raw) as { accessToken?: string };
        return meta?.accessToken ? { Authorization: `Bearer ${meta.accessToken}` } : {};
    } catch {
        return {};
    }
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface MerchantConfig {
    display_name: string;
    allowed_domains: string[];
    environment: string;
    logo?: string;
}

export interface MerchantConfigPatch {
    display_name?: string;
    allowed_domains?: string[];
    logo?: string;
}

// ─── Error class ─────────────────────────────────────────────────────────────

export class MerchantConfigApiError extends Error {
    constructor(
        message: string,
        public readonly status: number,
        /** The backend field name when the 400 body includes a { field } key. */
        public readonly field?: string,
        options?: ErrorOptions
    ) {
        super(message, options);
        this.name = 'MerchantConfigApiError';
    }
}

async function throwOnError(response: Response): Promise<never> {
    let field: string | undefined;
    let message: string | undefined;
    try {
        const errBody = (await response.json()) as {
            field?: string;
            value?: unknown;
            error?: string;
            message?: string;
        };
        field = typeof errBody?.field === 'string' ? errBody.field : undefined;
        message =
            typeof errBody?.error === 'string'
                ? errBody.error
                : typeof errBody?.message === 'string'
                ? errBody.message
                : undefined;
    } catch {
        // ignore JSON parse failures on error bodies
    }
    throw new MerchantConfigApiError(
        message ?? `HTTP ${response.status}`,
        response.status,
        field
    );
}

// ─── Service ─────────────────────────────────────────────────────────────────

export const apiMerchantConfigService = {
    /**
     * GET /account/merchant-config — retrieve the merchant's display name
     * and allowed-domains allowlist for the given environment.
     */
    async get(environment: Environment = 'test'): Promise<MerchantConfig> {
        const response = await fetch(
            `${BACKEND_URL}/account/merchant-config?environment=${encodeURIComponent(environment)}`,
            { headers: authHeaders() }
        );
        if (!response.ok) {
            await throwOnError(response);
        }
        return response.json() as Promise<MerchantConfig>;
    },

    /**
     * PATCH /account/merchant-config — update display name and/or allowed domains.
     * Send only the field(s) that changed; omit unchanged fields.
     */
    async update(patch: MerchantConfigPatch, environment: Environment = 'test'): Promise<MerchantConfig> {
        const response = await fetch(
            `${BACKEND_URL}/account/merchant-config?environment=${encodeURIComponent(environment)}`,
            {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify(patch)
            }
        );
        if (!response.ok) {
            await throwOnError(response);
        }
        return response.json() as Promise<MerchantConfig>;
    },

    /**
     * PATCH /account/merchant-config/logo — upload/replace the store logo image.
     * `logoBase64` must be the raw base64-encoded image bytes (no "data:" prefix).
     */
    async uploadLogo(logoBase64: string, environment: Environment = 'test'): Promise<MerchantConfig> {
        const response = await fetch(
            `${BACKEND_URL}/account/merchant-config/logo?environment=${encodeURIComponent(environment)}`,
            {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ logo: logoBase64 })
            }
        );
        if (!response.ok) {
            await throwOnError(response);
        }
        return response.json() as Promise<MerchantConfig>;
    }
};
